import React, { useEffect, useRef, useState } from 'react';
import { array, bool, string, number, func, oneOfType } from 'prop-types';
import { ALERT_TYPE, ShowToastMessage, useModalEscape } from '@laramie/ui';
import '@laramie/ui/dist/components/common/Select/index.scss';
import { useLista } from '../../hooks/useLista';

import '../Combobox/index.scss'
// Involución del Combobox: misma estética de lista desplegable, pero sin tipeo,
// sin filtrado y sin códigos. Se abre con click y se elige con click.
// Acepta opciones de una lista API (prop `lista`), filas ya cargadas por quien lo usa
// (prop `rows`, para no volver a pedir la lista) o estáticas (prop `options`: [{value, label}]).

// fallback con || a propósito: un placeholder vacío ("") también debe caer acá.
// No se aplica si el campo está deshabilitado: ahí pedir una acción imposible confunde
const PLACEHOLDER_FALLBACK = 'Seleccioná una opción';

const Select = (props) => {

    const [state, setState] = useState({
        open: false,
        highlight: -1
    });

    const wrapperRef = useRef(null);
    const listRef = useRef(null);

    // con `rows` las filas ya vienen de afuera: no se vuelve a pedir la lista
    const pedirLista = props.lista && !props.rows;

    const [getListLista] = useLista({
        listas: pedirLista ? [props.lista] : [],
        onLoaded: (listas, isSuccess, error) => {
            if (isSuccess) {
                setState(prevState => ({...prevState}));
            }
            else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: pedirLista ? {
            key: props.lista,
            timeout: 0
        } : undefined
    });

    // cierra al clickear fuera del componente
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setState(prevState => ({...prevState, open: false}));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // mientras el desplegable está abierto se registra en el stack de Escape de modales,
    // así Escape cierra solo el desplegable y no el modal que lo contiene
    useModalEscape(
        () => setState(prevState => ({...prevState, open: false})),
        state.open,
        () => wrapperRef.current?.closest('.modal.modal-block')
    );

    // mantiene visible la opción resaltada al navegar con teclado
    useEffect(() => {
        if (state.open && listRef.current) {
            const item = listRef.current.children[state.highlight];
            if (item) item.scrollIntoView({ block: 'nearest' });
        }
    }, [state.open, state.highlight]);

    // opciones estáticas (sin lista) u origen API; ambas normalizadas a { id, nombre }
    const baseItems = props.options
        ? props.options.map(o => ({ id: o.value, nombre: o.label }))
        : (props.rows ?? getListLista(props.lista)).filter(props.filter);

    const items = props.emptyOption
        ? [{ id: props.emptyValue, nombre: props.emptyOption }, ...baseItems]
        : baseItems;

    const selectedRow = items.find(row => row.id === props.value) ?? null;

    const isEmptyOption = (id) => props.emptyOption && id === props.emptyValue;

    const onSelected = (id, row) => {
        if (id !== props.value && props.onChange) {
            props.onChange({
                target: {
                    name: props.name,
                    type: 'lista',
                    value: id,
                    row: isEmptyOption(id) ? null : row
                }
            });
        }
        setState({ open: false, highlight: -1 });
    }

    // abre la lista arrancando en la opción ya seleccionada (o la primera)
    const openList = () => {
        const selectedIndex = items.findIndex(f => f.id === props.value);
        setState({ open: true, highlight: (selectedIndex >= 0) ? selectedIndex : 0 });
    }

    const onKeyDown = (e) => {
        if (!state.open) {
            if (e.key === 'Enter' || e.key === 'ArrowDown') {
                e.preventDefault();
                openList();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setState(prevState => ({...prevState, highlight: Math.min(prevState.highlight + 1, items.length - 1)}));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setState(prevState => ({...prevState, highlight: Math.max(prevState.highlight - 1, 0)}));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const row = items[state.highlight];
            if (row) onSelected(row.id, row);
        }
        else if (e.key === 'Escape' || e.key === 'Tab') {
            setState(prevState => ({...prevState, open: false}));
        }
    }

    return (
        <div className='combobox-wrapper select-wrapper' ref={wrapperRef}>

            <input
                id={props.name}
                name={props.name}
                type="text"
                readOnly={true}
                placeholder={props.disabled ? props.placeholder : (props.placeholder || PLACEHOLDER_FALLBACK)}
                className={`combobox-input select-control ${props.className}`}
                value={isEmptyOption(props.value) ? '' : props.onFormat(selectedRow)}
                onClick={() => state.open ? setState(prevState => ({...prevState, open: false})) : openList()}
                onKeyDown={onKeyDown}
                disabled={props.disabled}
            />

            <span className="material-symbols-outlined select-chevron">expand_more</span>

            {state.open && (
                <ul className="combobox-list" ref={listRef}>
                    {items.map((row, index) => (
                        <li
                            key={String(row.id)}
                            className={'combobox-option'
                                + ((index === state.highlight) ? ' combobox-option--highlighted' : '')
                                + ((row.id === props.value) ? ' combobox-option--selected' : '')}
                            onClick={() => onSelected(row.id, row)}
                            onMouseEnter={() => setState(prevState => ({...prevState, highlight: index}))}
                        >
                            {props.onFormat(row)}
                        </li>
                    ))}
                    {items.length === 0 && (
                        <li className="combobox-option combobox-option--empty">Sin resultados</li>
                    )}
                </ul>
            )}
        </div>
    )
}

Select.propTypes = {
    lista: string,
    rows: array,
    options: array,
    name: string.isRequired,
    placeholder: string,
    className: string,
    value: oneOfType([number, bool, string]),
    onChange: func,
    onFormat: func,
    filter: func,
    disabled: bool,
    emptyOption: string,
    emptyValue: oneOfType([number, string])
};

Select.defaultProps = {
    placeholder: '',
    className: '',
    value: null,
    onChange: null,
    onFormat: (row) => row ? (row.nombre ?? row.descripcion) : '',
    filter: (row) => { return true },
    disabled: false,
    emptyValue: 0
};

export default Select;
